import React, { useState, useEffect } from 'react';
import {
  Sheet,
  SheetContent,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { 
  Pill, 
  FileText, 
  ChevronDown, 
  AlertCircle, 
  ShoppingCart,
  X,
  Package,
  Sparkles,
} from 'lucide-react';
import { MedicationDrawerProps, MedicationSelection } from './medication-types';
import { useClinic } from '@/contexts/ClinicContext';
import { permissionCheck } from '@/lib/permission-check';
import { ComboboxCreatable } from '@/components/ui/combobox-creatable';

const MedicationDrawer: React.FC<MedicationDrawerProps> = ({
  open,
  onOpenChange,
  medication,
  programData,
  onSubmit,
  submitButtonText = 'Add to Cart',
  mode = 'cart',
  existingSelection = null,
}) => {
  const { isAdmin, rolePermissions, isPractitioner } = useClinic();
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedSigIndex, setSelectedSigIndex] = useState(0);
  const [selection, setSelection] = useState<MedicationSelection | null>(null);

  const canEdit = permissionCheck(
    'storefront:update',
    rolePermissions,
    isAdmin || isPractitioner
  );
  const canCreate = permissionCheck(
    'storefront:create',
    rolePermissions,
    isAdmin || isPractitioner
  );
  const canDoAction = canEdit || canCreate;
  const defaultReason = `${programData.title} - ${programData.subtitle || 'Order program'}`;

  // Initialize selection when medication changes
  useEffect(() => {
    if (medication && open) {
      const availableDoses = medication.doses?.filter((dose: string) => dose && dose.trim() !== '') || [];
      const availableSigs = medication.sig?.filter((sig: string) => sig && sig.trim() !== '') || [];
      
      // Use existing selection if available, otherwise use defaults
      if (existingSelection) {
        setSelection(existingSelection);
        // Find the sig index for the existing selection
        const sigIndex = availableSigs.findIndex((sig: string) => sig === existingSelection.sig);
        setSelectedSigIndex(sigIndex !== -1 ? sigIndex : 0);
      } else {
        const firstDose = availableDoses[0];
        const product = medication.map_dose_to_product?.find((p: any) => p.dose === firstDose);
        const firstSig = availableSigs[0] || '';

        setSelection({
          quantity: null,
          dose: firstDose,
          sig: firstSig,
          product_id: product?.product_id || '',
          price: parseFloat(product?.price || '0'),
          base_price: parseFloat(product?.base_price || '0'),
          program_name: programData.title,
          program_id: programData.id,
          medication_name: medication.name,
          daysSupply: '30',
          reason: product?.compounding_reason || defaultReason,
          unit_of_measure: medication?.unit_of_measure || '',
          refills_authorized: '',
          template_id: medication.template_id || null,
          product_type: product?.product_type || medication?.product_type || null,
        });
        setSelectedSigIndex(0);
      }
      setIsExpanded(false);
    }
  }, [medication, open, programData, existingSelection, defaultReason]);

  if (!medication) return null;

  const availableDoses = medication.doses?.filter((dose: string) => dose && dose.trim() !== '') || [];
  const availableSigs = medication.sig?.filter((sig: string) => sig && sig.trim() !== '') || [];

  const handleDoseSelection = (dose: string) => {
    const product = medication.map_dose_to_product?.find((p: any) => p.dose === dose);
    const sigInstruction = availableSigs[selectedSigIndex] || '';

    if (product && selection) {
      const shouldPrefillReason =
        !selection.reason || selection.reason === defaultReason;
      const nextReason = product?.compounding_reason && shouldPrefillReason
        ? product.compounding_reason
        : selection.reason;
      setSelection({
        ...selection,
        dose,
        sig: sigInstruction,
        product_id: product.product_id,
        price: parseFloat(product.price || '0'),
        base_price: parseFloat(product.base_price || '0'),
        unit_of_measure: medication?.unit_of_measure || '',
        reason: nextReason,
        product_type: product?.product_type || medication?.product_type || null,
      });
    }
  };

  const handleFieldChange = (field: keyof MedicationSelection, value: string) => {
    if (!selection) return;
    setSelection({
      ...selection,
      [field]: field === 'quantity' ? (value === '' ? null : parseInt(value) || null) : value,
    });
  };

  const handleSubmitClick = () => {
    if (!selection) return;
    onSubmit(selection);
    onOpenChange(false);
  };

  const price = selection?.price || 0;
  const quantity = selection?.quantity ?? 0;
  const totalPrice = price * quantity;
  const basePrice = selection?.base_price || 0;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className='flex w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-lg [&>button]:hidden' aria-describedby={undefined}>
        <SheetTitle className="sr-only">{medication.name}</SheetTitle>
        {/* Header with gradient */}
        <div className='relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 px-6 pb-6 pt-6'>
          {/* Background pattern */}
          <div className='absolute inset-0 opacity-10'>
            <div
              className='absolute inset-0'
              style={{
                backgroundImage:
                  'radial-gradient(circle at 2px 2px, rgba(255,255,255,0.2) 1px, transparent 0)',
                backgroundSize: '20px 20px',
              }}
            />
          </div>
          
          {/* Close button */}
          <button
            onClick={() => onOpenChange(false)}
            className='absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full bg-white/10 text-white/70 transition-colors hover:bg-white/20 hover:text-white z-[99]'
          >
            <X className='h-4 w-4' />
          </button>

          <div className='relative flex items-start gap-4'>
            {/* Product Image/Icon */}
            <div className='flex-shrink-0'>
              {medication.image_url ? (
                <img
                  src={medication.image_url}
                  alt={medication.name}
                  className='h-16 w-16 rounded-xl border-2 border-white/20 object-contain shadow-lg'
                />
              ) : (
                <div className='flex h-16 w-16 items-center justify-center rounded-xl border-2 border-white/20 bg-gradient-to-br from-emerald-500 to-teal-500 shadow-lg'>
                  <Pill className='h-8 w-8 text-white' />
                </div>
              )}
            </div>

            {/* Product Info */}
            <div className='flex-1 min-w-0 pr-8'>
              <div className='mb-1 inline-flex items-center gap-1.5 rounded-full bg-emerald-500/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-400'>
                <Sparkles className='h-3 w-3' />
                {programData.title}
              </div>
              <h2 className='text-lg font-bold text-white leading-tight line-clamp-2'>
                {medication.name}
              </h2>
              <p className='mt-1 text-2xl font-bold text-emerald-400'>
                ${totalPrice.toFixed(2)}
              </p>
            </div>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className='flex-1 overflow-y-auto'>
          {selection && (
            <div className='space-y-5 p-6'>
              {/* Dose Selection - Card Style */}
              <div className='space-y-2'>
                <Label className='text-[10px] font-medium uppercase tracking-wide text-slate-400'>
                  {availableDoses.length === 1 ? 'Dose' : 'Select Dose'}
                </Label>
                {availableDoses.length === 1 ? (
                  /* Single dose — locked display */
                  <div className='relative flex flex-col items-start rounded-xl border-2 border-emerald-500 bg-emerald-50 p-3 text-left'>
                    <div className='absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full bg-emerald-500'>
                      <svg className='h-3 w-3 text-white' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={3}>
                        <path strokeLinecap='round' strokeLinejoin='round' d='M5 13l4 4L19 7' />
                      </svg>
                    </div>
                    <span className='text-sm font-semibold text-emerald-700'>
                      {availableDoses[0]}
                    </span>
                    <span className='text-xs text-emerald-600'>
                      ${parseFloat(medication.map_dose_to_product?.find((p: any) => p.dose === availableDoses[0])?.price || '0').toFixed(2)}
                    </span>
                  </div>
                ) : (
                  /* Multiple doses — selectable grid */
                  <div className='grid grid-cols-2 gap-2'>
                    {availableDoses.map(dose => {
                      const product = medication.map_dose_to_product?.find((p: any) => p.dose === dose);
                      const dosePrice = parseFloat(product?.price || '0');
                      const isSelected = selection.dose === dose;
                      return (
                        <button
                          key={dose}
                          onClick={() => handleDoseSelection(dose)}
                          disabled={!canDoAction}
                          className={`relative flex flex-col items-start rounded-xl border-2 p-3 text-left transition-all ${
                            isSelected
                              ? 'border-emerald-500 bg-emerald-50'
                              : 'border-slate-200 bg-white hover:border-slate-300'
                          }`}
                        >
                          {isSelected && (
                            <div className='absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full bg-emerald-500'>
                              <svg className='h-3 w-3 text-white' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={3}>
                                <path strokeLinecap='round' strokeLinejoin='round' d='M5 13l4 4L19 7' />
                              </svg>
                            </div>
                          )}
                          <span className={`text-sm font-semibold ${isSelected ? 'text-emerald-700' : 'text-slate-900'}`}>
                            {dose}
                          </span>
                          <span className={`text-xs ${isSelected ? 'text-emerald-600' : 'text-slate-500'}`}>
                            ${dosePrice.toFixed(2)}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Instructions (Sig) - Creatable */}
              <div className='space-y-2'>
                <Label className='text-[10px] font-medium uppercase tracking-wide text-slate-400'>
                  Instructions (Sig)
                </Label>
                <ComboboxCreatable
                  options={availableSigs.map((sig: string) => ({ label: sig, value: sig }))}
                  value={selection.sig || ''}
                  onChange={val => {
                    if (selection) {
                      const sigIndex = availableSigs.indexOf(val);
                      if (sigIndex !== -1) setSelectedSigIndex(sigIndex);
                      setSelection({ ...selection, sig: val });
                    }
                  }}
                  placeholder='Type or select instructions...'
                  disabled={!canDoAction}
                />
              </div>

              {/* Quantity & Days Supply - Inline */}
              <div className='grid grid-cols-2 gap-4'>
                <div className='space-y-2'>
                  <Label className='text-[10px] font-medium uppercase tracking-wide text-slate-400'>
                    Quantity
                  </Label>
                  <ComboboxCreatable
                    options={(medication.qty_options || []).map((q: number) => ({
                      label: String(q),
                      value: String(q),
                    }))}
                    value={selection.quantity != null ? String(selection.quantity) : ''}
                    onChange={val => {
                      if (selection) {
                        const intOnly = val.replace(/[^0-9]/g, '');
                        const parsed = parseInt(intOnly, 10);
                        setSelection({ ...selection, quantity: isNaN(parsed) ? null : parsed });
                      }
                    }}
                    inputFilter={/[^0-9]/g}
                    placeholder='Select quantity'
                    disabled={!canDoAction}
                  />
                </div>

                <div className='space-y-2'>
                  <Label className='text-[10px] font-medium uppercase tracking-wide text-slate-400'>
                    Days Supply
                  </Label>
                  <Input
                    type='number'
                    value={selection.daysSupply}
                    onChange={e => handleFieldChange('daysSupply', e.target.value)}
                    onWheel={e => e.currentTarget.blur()}
                    placeholder='30'
                    min='1'
                    className='h-12 w-full rounded-xl border-slate-200 bg-white text-sm'
                    disabled={!canDoAction}
                  />
                </div>
              </div>

              {/* Refills (for prescription mode) */}
              {mode === 'prescription' && (
                <div className='space-y-2'>
                  <Label className='text-[10px] font-medium uppercase tracking-wide text-slate-400'>
                    Refills Authorized
                  </Label>
                  <Input
                    type='number'
                    value={selection.refills_authorized ?? ''}
                    onChange={e => handleFieldChange('refills_authorized', e.target.value)}
                    onWheel={e => e.currentTarget.blur()}
                    placeholder='0'
                    min='0'
                    max='99'
                    className='h-12 w-full rounded-xl border-slate-200 bg-white text-sm'
                    disabled={!canDoAction}
                  />
                </div>
              )}

              {/* Reason */}
              <div className='space-y-2'>
                <Label className='text-[10px] font-medium uppercase tracking-wide text-slate-400'>
                  Reason for Prescription
                </Label>
                <Textarea
                  value={selection.reason}
                  onChange={e => handleFieldChange('reason', e.target.value)}
                  placeholder='Enter reason for prescription...'
                  className='min-h-[80px] resize-none rounded-xl border-slate-200 bg-white text-sm'
                  disabled={!canDoAction}
                />
              </div>

              {/* Order Summary */}
              <div className='rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-4'>
                <div className='mb-3 flex items-center gap-2'>
                  <div className='flex h-6 w-6 items-center justify-center rounded-lg bg-emerald-500'>
                    <Package className='h-3.5 w-3.5 text-white' />
                  </div>
                  <span className='text-sm font-semibold text-slate-900'>Order Summary</span>
                </div>
                
                <div className='space-y-2'>
                  <div className='flex items-center justify-between text-sm'>
                    <span className='text-slate-500'>Dose</span>
                    <span className='font-medium text-slate-900'>{selection.dose || '-'}</span>
                  </div>
                  <div className='flex items-center justify-between text-sm'>
                    <span className='text-slate-500'>Quantity</span>
                    <span className='font-medium text-slate-900'>{selection.quantity ?? '-'}</span>
                  </div>
                  <div className='flex items-center justify-between text-sm'>
                    <span className='text-slate-500'>Days Supply</span>
                    <span className='font-medium text-slate-900'>{selection.daysSupply} days</span>
                  </div>
                  {(selection.refills_authorized !== undefined && selection.refills_authorized !== '') && (
                    <div className='flex items-center justify-between text-sm'>
                      <span className='text-slate-500'>Refills</span>
                      <span className='font-medium text-slate-900'>{selection.refills_authorized}</span>
                    </div>
                  )}
                  <div className='my-2 border-t border-dashed border-slate-200' />
                  <div className='flex items-center justify-between'>
                    <span className='text-sm font-semibold text-slate-700'>Total</span>
                    <span className='text-xl font-bold text-emerald-600'>${totalPrice.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Instructions Collapsible */}
              {selection.sig && (
                <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
                  <CollapsibleTrigger asChild>
                    <button className='flex w-full items-center justify-between rounded-xl border border-slate-200 bg-white p-3 text-left transition-colors hover:bg-slate-50'>
                      <div className='flex items-center gap-2'>
                        <FileText className='h-4 w-4 text-slate-400' />
                        <span className='text-sm font-medium text-slate-700'>
                          View Full Instructions
                        </span>
                      </div>
                      <ChevronDown
                        className={`h-4 w-4 text-slate-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                      />
                    </button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className='mt-2'>
                    <div className='rounded-xl bg-slate-100 p-3'>
                      <p className='text-sm leading-relaxed text-slate-700'>{selection.sig}</p>
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              )}

              {/* Error Messages */}
              {price <= 0 && (
                <div className='flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-3'>
                  <AlertCircle className='mt-0.5 h-4 w-4 flex-shrink-0 text-red-500' />
                  <span className='text-xs text-red-700'>
                    No pricing configured for this dose. Please configure prices before proceeding.
                  </span>
                </div>
              )}
              {basePrice >= price && basePrice > 0 && (
                <div
                  className={`flex items-start gap-3 rounded-xl border p-3 ${
                    mode === 'cart'
                      ? 'border-red-200 bg-red-50'
                      : 'border-amber-200 bg-amber-50'
                  }`}
                >
                  <AlertCircle
                    className={`mt-0.5 h-4 w-4 flex-shrink-0 ${
                      mode === 'cart' ? 'text-red-500' : 'text-amber-600'
                    }`}
                  />
                  <span className={`text-xs ${mode === 'cart' ? 'text-red-700' : 'text-amber-700'}`}>
                    {mode === 'cart'
                      ? 'Pharmacy cost exceeds patient price. Please review pricing.'
                      : 'Warning: Pharmacy cost exceeds patient price.'}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sticky Footer */}
        <div className='flex-shrink-0 border-t border-slate-200 bg-white p-4'>
          <Button
            onClick={handleSubmitClick}
            className='h-14 w-full rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 text-base font-semibold text-white shadow-lg shadow-emerald-500/25 transition-all hover:from-emerald-600 hover:to-teal-600 hover:shadow-xl'
            disabled={
              !selection ||
              !selection.quantity ||
              price <= 0 ||
              !canDoAction ||
              (mode === 'cart' && basePrice >= price && basePrice > 0)
            }
          >
            <ShoppingCart className='mr-2 h-5 w-5' />
            {submitButtonText} - ${totalPrice.toFixed(2)}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default MedicationDrawer;
